import { Component, h } from 'preact'
import Header from '../header'
import axios from 'axios'
import { DashboardTimezoneDisclaimer } from './DashboardTimezoneDisclaimer'
import { KeyInsightCard } from './KeyInsightCard'
import AnalyticsDateFormatter from './AnalyticsDateFormatter'
import {FilterOptionDate, FilterOptionItem, MetricItem, StoredState} from './interfaces'
import {
  createDefaultMetricItem,
  getDefaultDateRange, getDefaultSelectedDate,
  normalizeUserRanks,
  normalizeUserRoles,
  previousPeriodDifference,
  trackDashboardUsage
} from './utils'
import TopUsersTable from './TopUsersTable'
import DashboardSimpleFilter from './DashboardSimpleFilter'
import DashboardDateRangeFilter from './DashboardDateRangeFilter'
import QueryParameters from './QueryParameters'
import {subscribe} from '../../utils/globals'

interface Props {}


interface State {
  totalUsers: MetricItem,
  newRegistrations: MetricItem,
  usersWithOnePost: MetricItem,
  percentageUsersWithOnePost?: number,
  userRoles: Array<FilterOptionItem>,
  ranks: Array<FilterOptionItem>,
  selectedRoles: Array<string>,
  selectedRanks: Array<string>,
  isUserRoleDropdownOpen: boolean,
  isRankDropdownOpen: boolean,
  isDateRangeOpen: boolean,
  selectedPresetDateRangeId: string,
  startDate: Date,
  endDate: Date,
  selectedDate: FilterOptionDate,
  presetDateRangeList: Array<FilterOptionDate>,
  isLoading: boolean,
  isTotalUserKeyInsightLoading: boolean,
  isNewRegistrationKeyInsightLoading: boolean,
  isUserWithOnePostKeyInsightLoading: boolean,
  isPercentageUserWithOnePostKeyInsightLoading: boolean,
  storedState: StoredState,
  totalUsersForPostMetric: number,
  isSidebarMenuOpen: boolean
}

class UserDashboard extends Component<Props, State> {
  wrapperRef: null | HTMLElement = null
  constructor () {
    super()
    let monthStartDate = AnalyticsDateFormatter.getMonthStartDate()
    let yesterday = AnalyticsDateFormatter.getYesterday()
    const defaultMetricItem = createDefaultMetricItem()
    const queryParams = new QueryParameters()
    this.setState({
      totalUsers: defaultMetricItem,
      newRegistrations: defaultMetricItem,
      usersWithOnePost: defaultMetricItem,
      percentageUsersWithOnePost: 0,
      userRoles: [],
      ranks: [],
      selectedRoles: queryParams.has('roles') ? queryParams.get('roles').split(',') : [],
      selectedRanks: queryParams.has('ranks') ? queryParams.get('ranks').split(',') : [],
      isUserRoleDropdownOpen: false,
      isRankDropdownOpen: false,
      isDateRangeOpen: false,
      selectedPresetDateRangeId: queryParams.has('date_range') ? queryParams.get('date_range') : 'last_thirty_days',
      storedState: {
        presetDateRangeId: queryParams.has('date_range') ? queryParams.get('date_range') : 'last_thirty_days',
        selectedDateRange: getDefaultSelectedDate()
      },
      startDate: monthStartDate,
      endDate: yesterday,
      presetDateRangeList: [],
      isLoading: false,
      isTotalUserKeyInsightLoading: false,
      isNewRegistrationKeyInsightLoading: false,
      isUserWithOnePostKeyInsightLoading: false,
      isPercentageUserWithOnePostKeyInsightLoading: false,
      totalUsersForPostMetric: 0,
      isSidebarMenuOpen: true
    })
    this.toggleUserRoleDropdown = this.toggleUserRoleDropdown.bind(this)
    this.onUserRolesChanged = this.onUserRolesChanged.bind(this)
    this.onResetUserRoles = this.onResetUserRoles.bind(this)
    this.onClearUserRoles = this.onClearUserRoles.bind(this)

    this.toggleRankDropdown = this.toggleRankDropdown.bind(this)
    this.onRanksChanged = this.onRanksChanged.bind(this)
    this.onResetRanks = this.onResetRanks.bind(this)
    this.onClearRanks = this.onClearRanks.bind(this)

    this.toggleDateRange = this.toggleDateRange.bind(this)
    this.onDateRangeChanged = this.onDateRangeChanged.bind(this)
    this.onResetDateRange = this.onResetDateRange.bind(this)
    this.onPresetDateRangeChanged = this.onPresetDateRangeChanged.bind(this)
    this.onCustomDateRangeChanged = this.onCustomDateRangeChanged.bind(this)
  }

  async componentDidMount () {
    trackDashboardUsage('user')
    subscribe('control_sidebar_state', (payload) => this.setState({ isSidebarMenuOpen: payload.isOpen }))

    this.setState({
      isLoading: true,
      isTotalUserKeyInsightLoading: true,
      isNewRegistrationKeyInsightLoading: true,
      isUserWithOnePostKeyInsightLoading: true,
      isPercentageUserWithOnePostKeyInsightLoading: true
    })
    try {
      const [presetDateRanges, mainRoles, rankList] = await Promise.all([this.fetchDateRangeFilter(), this.fetchUserRoles(), this.fetchRanks()])
      let defaultDateRange = getDefaultDateRange(presetDateRanges, this.state.selectedPresetDateRangeId)
      const updatedState = this.state.storedState
      updatedState.presetDateRangeId = defaultDateRange.id
      updatedState.selectedDateRange = defaultDateRange

      if ('custom_range' === this.state.selectedPresetDateRangeId) {
        const queryParameters = new QueryParameters()
        defaultDateRange = {
          id: 'custom_range',
          label: 'Custom range',
          startDate: new Date(queryParameters.get('date_from')),
          endDate: new Date(queryParameters.get('date_till'))
        }
        updatedState.selectedDateRange = getDefaultSelectedDate()
      }

      this.setState({
        userRoles: mainRoles,
        ranks: rankList,
        presetDateRangeList: presetDateRanges,
        selectedDate: defaultDateRange,
        startDate: new Date(defaultDateRange.startDate),
        endDate: new Date(defaultDateRange.endDate),
        storedState: updatedState,
        isLoading: false
      })
    } catch {
      this.setState({ isLoading: false })
    }

    this.fetchTotalUsers()
    this.fetchNewRegistrations()
    const usersWithOnePostPromise = this.fetchUsersWithOnePost()
    const totalUsersToCalculatePercentage = await this.fetchTotalUsersForPercentageUsersWithOnePost()
    await usersWithOnePostPromise
    await totalUsersToCalculatePercentage
    this.calculatePercentageUsersWithOnePost()
  }

  addHandler () {
    const { isDateRangeOpen, isUserRoleDropdownOpen, isRankDropdownOpen } = this.state
    if(isDateRangeOpen || isUserRoleDropdownOpen || isRankDropdownOpen ) {
      window.document.addEventListener('mousedown', this.handleClickOutside)
    }
  }

  removeHandler () {
    window.document.removeEventListener('mousedown', this.handleClickOutside)
  }

  handleClickOutside = (e: MouseEvent) => {
    if (!this.wrapperRef) return
    if (!this.wrapperRef.contains(e.target as HTMLElement)) {
      this.onResetDateRange()
      this.setState({
        isUserRoleDropdownOpen: false,
        isRankDropdownOpen: false
      }, this.removeHandler)
    }
  }

  async fetchUserRoles () {
    const res = await axios.get('/role/fetchRolesForFilter')
    return normalizeUserRoles(res.data)
  }

  async fetchRanks () {
    const res = await axios.get('/settings/rank/fetchRanks')
    return normalizeUserRanks(res.data)
  }

  async refetchData () {
    this.fetchTotalUsers()
    this.fetchNewRegistrations()
    const usersWithOnePostPromise = this.fetchUsersWithOnePost()
    const totalUsersToCalculatePercentage = await this.fetchTotalUsersForPercentageUsersWithOnePost()
    await usersWithOnePostPromise
    await totalUsersToCalculatePercentage
    this.calculatePercentageUsersWithOnePost()
  }

  async fetchDateRangeFilter () {
    const res = await axios.get('/analyticsV2/fetchDateRangeFilter')
    return res.data.ranges
  }

  async fetchTotalUsers () {
    this.setState({ isTotalUserKeyInsightLoading: true })
    try {
    const res = await this.fetchData('/analyticsV2/fetchTotalUsers')
    const { count, previousCount } = res.data
    const percentageDifference = previousPeriodDifference(count, previousCount)
    this.setState({
      totalUsers: {
        count: count,
        previousCount: previousCount,
        percentageDifference: percentageDifference
      },
      isTotalUserKeyInsightLoading: false
    }) } catch {
      this.setState({ isTotalUserKeyInsightLoading: false })
    }
  }

  async fetchNewRegistrations () {
    this.setState({ isNewRegistrationKeyInsightLoading: true })
    try {
      const res = await this.fetchData('/analyticsV2/fetchNewRegistrations')
      const {count, previousCount} = res.data
      const percentageDifference = previousPeriodDifference(count, previousCount)
      this.setState({
        newRegistrations: {
          count: count,
          previousCount: previousCount,
          percentageDifference: percentageDifference
        },
        isNewRegistrationKeyInsightLoading: false
      })
    } catch {
      this.setState({ isNewRegistrationKeyInsightLoading: false })
    }
  }

  async fetchUsersWithOnePost () {
    this.setState({ isUserWithOnePostKeyInsightLoading: true })
    try {
      const res = await this.fetchData('/analyticsV2/fetchUsersPostMetric')
      const {count, previousCount} = res.data
      const percentageDifference = previousPeriodDifference(count, previousCount)
      this.setState({
        usersWithOnePost: {
          count: count,
          previousCount: previousCount,
          percentageDifference: percentageDifference
        },
        isUserWithOnePostKeyInsightLoading: false
      })
    } catch {
      this.setState({ isUserWithOnePostKeyInsightLoading: false })
    }
  }

  async fetchTotalUsersForPercentageUsersWithOnePost () {
    this.setState({ isPercentageUserWithOnePostKeyInsightLoading: true })
    try {
      const res = await this.fetchData('/analyticsV2/fetchTotalUsersForPostMetric')
      this.setState( { totalUsersForPostMetric: res.data.count })
    } catch {
      this.setState({ isPercentageUserWithOnePostKeyInsightLoading: false })
    }
  }


  async calculatePercentageUsersWithOnePost () {
    this.setState({ isPercentageUserWithOnePostKeyInsightLoading: true })
      const {usersWithOnePost, totalUsersForPostMetric} = this.state
      if (usersWithOnePost !== undefined && usersWithOnePost.count !== 0) {
        const percent = (usersWithOnePost.count / totalUsersForPostMetric) * 100
        this.setState({
          percentageUsersWithOnePost: percent,
          isPercentageUserWithOnePostKeyInsightLoading: false
        })
      }
     this.setState({ isPercentageUserWithOnePostKeyInsightLoading: false })
  }


  async fetchData (url: string) {
    const { startDate, endDate, selectedRoles, selectedRanks } = this.state
    const filterOptions = {
      start: AnalyticsDateFormatter.formatDate(startDate),
      end: AnalyticsDateFormatter.formatDate(endDate, false),
      roles: selectedRoles,
      ranks: selectedRanks
    }
    return axios.post(url, {
      filters: filterOptions
    })
  }

  toggleUserRoleDropdown (value: boolean) {
    if (this.state.isRankDropdownOpen) {
      this.onResetRanks()
    }
    if (this.state.isDateRangeOpen) {
      this.onResetDateRange()
    }
    this.setState({ isUserRoleDropdownOpen: value }, this.addHandler)
  }

  onUserRolesChanged (data: any) {
    this.setState({
      isUserRoleDropdownOpen: false,
      selectedRoles: data
    })
    window.history.pushState(null, 'roles changed', (new QueryParameters()).set('roles', data.join(',')).getQueryString())
    this.refetchData()
  }

  onResetUserRoles () {
    this.setState({
      isUserRoleDropdownOpen: false
    })
  }

  onClearUserRoles (e: Event) {
    e.preventDefault()
    e.stopPropagation()
    this.setState({
      selectedRoles: [],
      isUserRoleDropdownOpen: false
    })
    window.history.pushState(null, 'roles cleared', (new QueryParameters()).unset('roles').getQueryString())
    this.refetchData()
  }

  toggleRankDropdown (value: boolean) {
    if (this.state.isUserRoleDropdownOpen) {
      this.onResetUserRoles()
    }
    if (this.state.isDateRangeOpen) {
      this.onResetDateRange()
    }
    this.setState({ isRankDropdownOpen: value }, this.addHandler)
  }

  onRanksChanged (data: any) {
    this.setState({
      isRankDropdownOpen: false,
      selectedRanks: data
    })
    window.history.pushState(null, 'ranks changed', (new QueryParameters()).set('ranks', data.join(',')).getQueryString())
    this.refetchData()
  }

  onResetRanks () {
    this.setState({
      isRankDropdownOpen: false
    })
  }

  onClearRanks (e: Event) {
    e.preventDefault()
    e.stopPropagation()
    this.setState({
      selectedRanks: [],
      isRankDropdownOpen: false
    })
    window.history.pushState(null, 'ranks cleared', (new QueryParameters()).unset('ranks').getQueryString())
    this.refetchData()
  }

  toggleDateRange (value: boolean) {
    if (this.state.isRankDropdownOpen) {
      this.onResetRanks()
    }
    if (this.state.isUserRoleDropdownOpen) {
      this.onResetUserRoles()
    }
    this.setState({ isDateRangeOpen: value }, this.addHandler)
  }

  onPresetDateRangeChanged (selectedDate: FilterOptionDate) {
    this.setState({
      selectedPresetDateRangeId: selectedDate.id,
      selectedDate: selectedDate
    })
  }

  onCustomDateRangeChanged (dates: Date[]) {
    const customSelectedDate = {
      id: 'custom_range',
      label: 'Custom Range',
      startDate: dates[0],
      endDate: dates[1]
    }
    this.setState({
      selectedDate: customSelectedDate,
      selectedPresetDateRangeId: customSelectedDate.id
    })
  }

  onDateRangeChanged () {
    const { selectedDate, selectedPresetDateRangeId } = this.state
    const updatedState = this.state.storedState
    updatedState.presetDateRangeId = selectedPresetDateRangeId
    updatedState.selectedDateRange = selectedDate
    this.setState({
      isDateRangeOpen: false,
      startDate: new Date(selectedDate.startDate),
      endDate: new Date(selectedDate.endDate),
      storedState: updatedState
    })
    const queryString = new QueryParameters()
    queryString.set('date_range', selectedPresetDateRangeId)
    if (selectedPresetDateRangeId != 'custom_range') {
      queryString.unset('date_from')
        .unset('date_till')
    } else {
      queryString.set('date_from', AnalyticsDateFormatter.formatDateForUrl(selectedDate.startDate))
        .set('date_till', AnalyticsDateFormatter.formatDateForUrl(selectedDate.endDate))
    }
    window.history.pushState(null, 'date range changed', queryString.getQueryString())
    this.refetchData()
  }

  onResetDateRange () {
    const { presetDateRangeId, selectedDateRange } = this.state.storedState
    this.setState({
      isDateRangeOpen: false,
      selectedPresetDateRangeId: presetDateRangeId,
      selectedDate: selectedDateRange
    })
  }

  render (_: Props, { totalUsers, newRegistrations, usersWithOnePost, percentageUsersWithOnePost, presetDateRangeList, userRoles, selectedRoles, isUserRoleDropdownOpen, ranks, selectedRanks, isRankDropdownOpen, isDateRangeOpen, selectedPresetDateRangeId, startDate, endDate, isLoading, selectedDate, isTotalUserKeyInsightLoading, isNewRegistrationKeyInsightLoading, isUserWithOnePostKeyInsightLoading, isPercentageUserWithOnePostKeyInsightLoading, isSidebarMenuOpen}: State) {
    return (
      <div className={`user-dashboard-overview dashboard-overview ${isSidebarMenuOpen ? 'dashboard-overview-open-menu' : 'dashboard-overview-closed-menu'}`} style={{ width: `calc(100vw - ${isSidebarMenuOpen ? '376' : '152'}px)` }}>
        <Header
          title='User Dashboard'
          disableCancelButton
          disableSaveButton
          isBeta
        />
        <div className='filter-container' ref={node => this.wrapperRef = node}>
          <DashboardDateRangeFilter isLoading={isLoading}
                                    startDate={startDate}
                                    endDate={endDate}
                                    isDropdownOpen={isDateRangeOpen}
                                    items={presetDateRangeList}
                                    selectedDate={selectedDate}
                                    selectedPresetId={selectedPresetDateRangeId}
                                    toggleDropdown={this.toggleDateRange}
                                    onChange={this.onDateRangeChanged}
                                    onReset={this.onResetDateRange}
                                    onCustomDateChange={this.onCustomDateRangeChanged}
                                    onPresetDateChange={this.onPresetDateRangeChanged} />
          <DashboardSimpleFilter isLoading={isLoading}
                                 id='userRole'
                                 name='user role'
                                 customClassName='user-role'
                                 title='User role'
                                 isDropdownOpen={isUserRoleDropdownOpen}
                                 toggleDropdown={this.toggleUserRoleDropdown}
                                 resetDropdown={this.onResetUserRoles}
                                 onClear={this.onClearUserRoles}
                                 onChange={this.onUserRolesChanged}
                                 items={userRoles}
                                 selectedItems={selectedRoles} />
          <DashboardSimpleFilter isLoading={isLoading}
                                 id='ranks'
                                 name='rank'
                                 customClassName='rank'
                                 title='Rank'
                                 isDropdownOpen={isRankDropdownOpen}
                                 toggleDropdown={this.toggleRankDropdown}
                                 resetDropdown={this.onResetRanks}
                                 onClear={this.onClearRanks}
                                 onChange={this.onRanksChanged}
                                 items={ranks}
                                 selectedItems={selectedRanks} />
        </div>
        <div className='metric-container'>
          <KeyInsightCard title='Total users'
                          isLoading={isTotalUserKeyInsightLoading}
                          total={totalUsers.count}
                          titleTooltip='The total number of registered users on the community up to the end of the selected time period.'
                          percent={totalUsers.percentageDifference}
                          showComparisionUnavailable={totalUsers.previousCount === 0}
                          comparisionTooltip='The percentage difference between the total number of registered users in the current/previous time period.' />
          <KeyInsightCard title='New registrations'
                          isLoading={isNewRegistrationKeyInsightLoading}
                          total={newRegistrations.count}
                          titleTooltip='The number of new registrations in the selected time period.'
                          percent={newRegistrations.percentageDifference}
                          showComparisionUnavailable={newRegistrations.previousCount === 0}
                          comparisionTooltip='The percentage difference between the number of new registrations in the current/previous time period.' />
          <KeyInsightCard title='Users with at least 1 post'
                          isLoading={isUserWithOnePostKeyInsightLoading}
                          total={usersWithOnePost.count}
                          titleTooltip='The number of users who created at least one topic or reply in the selected time period.'
                          percent={usersWithOnePost.percentageDifference}
                          showComparisionUnavailable={usersWithOnePost.previousCount === 0}
                          comparisionTooltip='The percentage difference between the number of users who created at least one topic or reply in the current/previous time period.' />
          <KeyInsightCard title='% Users with at least 1 post'
            isLoading={isPercentageUserWithOnePostKeyInsightLoading}
            total={percentageUsersWithOnePost}
            isPercentage
            hideComparisionWithPreviousPeriod
            titleTooltip='The % of users who created at least one topic or reply in the selected time period, compared to the total number of users.' />
        </div>
        <TopUsersTable startDate={startDate} endDate={endDate} selectedRoles={selectedRoles} selectedRanks={selectedRanks} />
        <DashboardTimezoneDisclaimer />
      </div>)
  }
}

export default UserDashboard
